import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users, UserPlus, Copy, Check, Trash2, Plus, Loader2,
  Mail, Shield, ShieldOff, Crown, ArrowRightLeft, Ban, KeyRound
} from "lucide-react";
import RolesView from "./RolesView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Team() {
  const { user, isOwner, logout } = useAuth();
  const [mainTab, setMainTab] = useState("members"); // "members" or "roles"
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [accessRoles, setAccessRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [mRes, iRes, rRes] = await Promise.all([
        axios.get(`${API}/team`),
        isOwner ? axios.get(`${API}/invites`) : Promise.resolve({ data: [] }),
        isOwner ? axios.get(`${API}/access-roles`) : Promise.resolve({ data: [] }),
      ]);
      setMembers(mRes.data);
      setInvites(iRes.data);
      setAccessRoles(rRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/invites`, { 
        email: inviteEmail || null,
        access_role_id: inviteRoleId || null,
        send_email: sendInviteEmail && !!inviteEmail,
      });
      const msg = sendInviteEmail && inviteEmail ? "Invite created & email sent" : "Invite created";
      toast.success(msg);
      setInviteEmail("");
      setInviteRoleId("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const deleteInvite = async (id) => {
    try { await axios.delete(`${API}/invites/${id}`); toast.success("Invite deleted"); fetchAll(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success("Code copied!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleMemberStatus = async (memberId) => {
    try {
      const res = await axios.patch(`${API}/team/${memberId}/deactivate`);
      toast.success(res.data.status === "inactive" ? "Member deactivated" : "Member reactivated");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setConfirmAction(null);
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team/${memberId}`);
      toast.success("Member removed");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setConfirmAction(null);
  };

  const transferOwnership = async (memberId) => {
    try {
      await axios.post(`${API}/team/transfer-ownership`, { user_id: memberId });
      toast.success("Ownership transferred. You are now a team member.");
      logout();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setConfirmAction(null);
  };

  const assignRole = async (memberId, roleId) => {
    try {
      await axios.patch(`${API}/team/${memberId}/assign-role`, { role_id: roleId || null });
      toast.success(roleId ? "Role assigned" : "Role removed");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign role"); }
  };

  const getRoleName = (roleId) => {
    const role = accessRoles.find(r => r.id === roleId);
    return role?.name || null;
  };

  const pendingInvites = invites.filter((i) => !i.used);
  const usedInvites = invites.filter((i) => i.used);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="team-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
          <Users className="w-5 h-5 text-stone-50" />
        </div>
        <span className="text-xs font-medium uppercase tracking-widest text-stone-500">People</span>
      </div>
      <h1 data-testid="team-title" className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
        Team
      </h1>
      <p className="text-base text-stone-500 mb-6">
        {members.length} member{members.length !== 1 ? "s" : ""} in your Command Center
      </p>

      {/* Main Tabs */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-lg mb-6 w-fit">
        <button
          data-testid="tab-members"
          onClick={() => setMainTab("members")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${mainTab === "members" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
        >
          <Users className="w-4 h-4" /> Members
        </button>
        {isOwner && (
          <button
            data-testid="tab-roles"
            onClick={() => setMainTab("roles")}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2 ${mainTab === "roles" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
          >
            <KeyRound className="w-4 h-4" /> Roles
          </button>
        )}
      </div>

      {/* Roles Tab Content */}
      {mainTab === "roles" && isOwner && <RolesView embedded onRolesChange={fetchAll} />}

      {/* Members Tab Content */}
      {mainTab === "members" && (
        <div className="max-w-3xl space-y-6">
        {/* Members List */}
        <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-stone-500" /> Members
            </h2>
            <div className="space-y-2">
              {members.map((m) => {
                const isSelf = m.id === user?.id;
                const isActive = m.status !== "inactive";
                return (
                  <div
                    key={m.id}
                    data-testid={`member-${m.id}`}
                    className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${
                      !isActive ? "bg-stone-100 opacity-60" : "bg-stone-50"
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-stone-600">
                        {(m.name || m.email || "?")[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-stone-900 truncate">{m.name || m.email}</span>
                        {m.is_owner && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded-full font-medium">
                            <Crown className="w-3 h-3" /> Owner
                          </span>
                        )}
                        {!isActive && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">Inactive</span>
                        )}
                        {isSelf && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium">You</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 truncate">{m.email}</p>
                    </div>
                    <div className="text-xs text-stone-400 hidden sm:block">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}
                    </div>

                    {/* Role Assignment (owner only, not on self, not on owner) */}
                    {isOwner && !isSelf && !m.is_owner && accessRoles.length > 0 && (
                      <Select
                        value={m.access_role_id || "none"}
                        onValueChange={(val) => assignRole(m.id, val === "none" ? null : val)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs" data-testid={`role-select-${m.id}`}>
                          <SelectValue placeholder="No role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No role</SelectItem>
                          {accessRoles.map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {/* Show role badge for non-owners viewing */}
                    {!isOwner && m.access_role_id && (
                      <span className="px-2 py-1 text-xs bg-stone-100 text-stone-600 rounded">
                        {getRoleName(m.access_role_id)}
                      </span>
                    )}

                    {/* Actions (owner only, not on self) */}
                    {isOwner && !isSelf && !m.is_owner && (
                      <div className="flex items-center gap-1">
                        {/* Toggle active/inactive */}
                        <Button
                          data-testid={`toggle-member-${m.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmAction({ type: "toggle", id: m.id, name: m.name || m.email, isActive })}
                          className="text-stone-400 hover:text-amber-600 px-2"
                          title={isActive ? "Deactivate" : "Reactivate"}
                        >
                          {isActive ? <Ban className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                        </Button>

                        {/* Transfer ownership */}
                        <Button
                          data-testid={`transfer-owner-${m.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmAction({ type: "transfer", id: m.id, name: m.name || m.email })}
                          className="text-stone-400 hover:text-blue-600 px-2"
                          title="Transfer ownership"
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </Button>

                        {/* Remove */}
                        <Button
                          data-testid={`remove-member-${m.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmAction({ type: "remove", id: m.id, name: m.name || m.email })}
                          className="text-stone-400 hover:text-red-600 px-2"
                          title="Remove from team"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        {confirmAction && (
          <Card className="bg-white border-2 border-amber-200 rounded-xl shadow-lg">
            <CardContent className="p-5">
              <p className="text-sm text-stone-700 mb-4">
                {confirmAction.type === "toggle" && (
                  <>Are you sure you want to <strong>{confirmAction.isActive ? "deactivate" : "reactivate"}</strong> <strong>{confirmAction.name}</strong>?
                  {confirmAction.isActive && " They will lose access to the Command Center."}</>
                )}
                {confirmAction.type === "remove" && (
                  <>Are you sure you want to <strong>permanently remove</strong> <strong>{confirmAction.name}</strong> from your team?</>
                )}
                {confirmAction.type === "transfer" && (
                  <>Are you sure you want to <strong>transfer ownership</strong> to <strong>{confirmAction.name}</strong>? You will become a regular team member and be logged out.</>
                )}
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  data-testid="confirm-cancel"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction(null)}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-action"
                  size="sm"
                  onClick={() => {
                    if (confirmAction.type === "toggle") toggleMemberStatus(confirmAction.id);
                    else if (confirmAction.type === "remove") removeMember(confirmAction.id);
                    else if (confirmAction.type === "transfer") transferOwnership(confirmAction.id);
                  }}
                  className={`${
                    confirmAction.type === "transfer"
                      ? "bg-blue-600 hover:bg-blue-700"
                      : confirmAction.type === "remove"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  } text-white`}
                >
                  {confirmAction.type === "toggle" ? (confirmAction.isActive ? "Deactivate" : "Reactivate") :
                   confirmAction.type === "remove" ? "Remove" : "Transfer Ownership"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invite Members (owner only) */}
        {isOwner && (
          <Card className="bg-white border border-stone-200/60 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-6">
              <h2 className="text-base font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-stone-500" /> Invite Members
              </h2>
              <p className="text-xs text-stone-500 mb-4">Generate invite codes to let team members join your Command Center.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <Input
                  data-testid="invite-email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 min-w-[200px] text-sm"
                />
                {accessRoles.length > 0 && (
                  <Select value={inviteRoleId} onValueChange={setInviteRoleId}>
                    <SelectTrigger className="w-40 text-sm" data-testid="invite-role-select">
                      <SelectValue placeholder="Assign role..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No role</SelectItem>
                      {accessRoles.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  data-testid="create-invite-btn"
                  onClick={createInvite}
                  disabled={creating}
                  className="bg-stone-900 text-stone-50 hover:bg-stone-800 px-5"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Create
                </Button>
              </div>
              <label data-testid="send-email-toggle" className="flex items-center gap-2 mb-4 cursor-pointer select-none">
                <input type="checkbox" checked={sendInviteEmail} onChange={(e) => setSendInviteEmail(e.target.checked)} className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500" />
                <span className="text-sm text-stone-600">Send invite email to recipient</span>
                {!inviteEmail && sendInviteEmail && <span className="text-xs text-stone-400">(enter email above)</span>}
              </label>

              {pendingInvites.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Pending</p>
                  {pendingInvites.map((inv) => (
                    <div key={inv.id} data-testid={`invite-${inv.id}`} className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-sm font-mono font-semibold text-stone-900">{inv.code}</code>
                          {inv.access_role_id && (
                            <span className="px-2 py-0.5 text-xs bg-stone-200 text-stone-600 rounded flex items-center gap-1">
                              <KeyRound className="w-3 h-3" /> {getRoleName(inv.access_role_id)}
                            </span>
                          )}
                        </div>
                        {inv.email && <p className="text-xs text-stone-400 mt-0.5"><Mail className="w-3 h-3 inline mr-0.5" /> {inv.email} {inv.email_sent && <span className="text-emerald-600 font-medium">&middot; Email sent</span>}</p>}
                      </div>
                      <Button data-testid={`copy-invite-${inv.id}`} variant="ghost" size="sm" onClick={() => copyCode(inv.code, inv.id)} className="text-stone-500 hover:text-stone-700 px-2">
                        {copiedId === inv.id ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                      <Button data-testid={`delete-invite-${inv.id}`} variant="ghost" size="sm" onClick={() => deleteInvite(inv.id)} className="text-stone-400 hover:text-red-600 px-2">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {usedInvites.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Used</p>
                  {usedInvites.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-3 bg-stone-50 rounded-lg px-4 py-3 opacity-60">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono text-stone-600">{inv.code}</code>
                          <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full font-medium">
                            Joined{inv.used_by ? `: ${inv.used_by}` : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {invites.length === 0 && (
                <p className="text-sm text-stone-400 text-center py-4">No invites yet.</p>
              )}
            </CardContent>
          </Card>
        )}
        </div>
      )}
    </div>
  );
}
